import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';

// Utility to extract YouTube Video ID
function extractYouTubeVideoId(url) {
  const regex =
    /(?:youtube\.com\/(?:.*v=|v\/|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

export default function VideoWithFullscreen({ story }) {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange,
      );
    };
  }, []);

  if (!story?.videoUrl) return null;

  const videoId = extractYouTubeVideoId(story.videoUrl);
  if (!videoId) return null;

  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;

  const toggleFullscreen = () => {
    const el = containerRef.current;

    if (!document.fullscreenElement) {
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el?.mozRequestFullScreen) el.mozRequestFullScreen();
      else if (el?.msRequestFullscreen) el.msRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    }
  };

  return (
    <div
      className="relative w-full overflow-hidden rounded-md"
      ref={containerRef}
    >
      {/* Toggle Fullscreen Button */}
      <button
        onClick={toggleFullscreen}
        className="absolute right-[10px] bottom-[10px] z-20 rounded bg-black p-2 text-white hover:bg-opacity-70"
        title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
      >
        <FontAwesomeIcon
          icon={isFullscreen ? faCompress : faExpand}
          className="h-4 w-4"
        />
      </button>

      {/* Responsive Iframe */}
      <div className="relative h-0 w-full overflow-hidden pb-[56.25%]">
        <iframe
          src={embedUrl}
          title="YouTube video player"
          className="pointer-events-none absolute top-0 left-0 h-full w-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </div>
    </div>
  );
}
